<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0" >
			<div class="padding-lg-x">
				<StatsChartTitleWithDescription
					titleKey="delays-chart.chartTitle"
					descriptionKey="delays-chart.chartTitleTooltip"
					titleClass="pd-md-top"
				/>
				<template v-if="mountpointsSelected.length > 0">
					<HeatMapChart
						:isLoading="isLoading"
						:series="heatmapSeries"
						:categories="heatmapCategories"
						:colorSettings="COLOR_SETTINGS"
						xAxisLabel="GPS time"
						units="ms"
					/>
					<div class="padding-block"></div>
					<TimeSeriesChart
						:isLoading="isLoading"
						:series="lineSeries"
						:options="lineChartOptions"
						:height="lineChartHeight"
					/>
				</template>
				<EmptyChartFiltersMessage v-else messageKey="delays-chart.mountpointsPlaceholder" />
			</div>
		</Col>
		<Col :span="6" :offset="0" >
			<VerticalLayout>
				<Affix :offsetTop="77" :target="() => wrapper">
					<Card :bordered="false">
						<VerticalLayout>
							<BaseStationSelect v-model="mountpointsSelected" :onUpdate="setSubnetworkOnMountpointsChange" />
							<SubNetworkSelect
								ref="subNetworkSelect"
								:onChange="setMountpointsForSubnetwork"
								:allowClear="true"
							/>
							<LabeledSwitch
								v-model="showMax"
								:loading="showMaxLoading"
								:leftLabel="$t('delays-chart.showAvg')"
								:rightLabel="$t('delays-chart.showMax')"
								@change="onShowMaxChange()"
							/>
							<DateRangeChooser v-model="datesRange" @change="delaysGet()" />
						</VerticalLayout>
					</Card>
					<Card v-if="showGenerateTestDataButton" :bordered="false">
						<PopconfirmButton
							:title="$t('common.dangerousOperationLabel')"
							:ok-text="$t('common.confirmButtonLabel')"
							:cancel-text="$t('common.cancelButtonLabel')"
							:onConfirm="onGenerateTestData"
							:loading="isLoadingTestData"
							isDangerousOperation
						>
							{{ $t('delays-chart.generateTestDataButtonLabel') }}
						</PopconfirmButton>
					</Card>
				</Affix>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { Row, Col, Card, Affix } from 'ant-design-vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import { useSessionStore } from '@/stores/session';
import {
	getBaseOptions,
	generateDashArray, generateSeries, setXAxisRange,
	pointEntryFactoryXYDay,
	getClosestValue,
	MINUTE_AGGREGATES,
} from '@/utils/chartsUtils';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';
import { useSubNetworkMountpointsFilter } from '@/utils/useSubNetworkMountpointsFilter';
import { datetimeToString } from '@/components/common/dateUtils';
import { useProvideInjectWrapper } from '@/utils/useProvideInject';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import LabeledSwitch from '@/components/common/LabeledSwitch';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage';
import BaseStationSelect from '@/components/common/controls/BaseStationSelect';
import SubNetworkSelect from '@/components/common/controls/SubNetworkSelect.vue';
import HeatMapChart from '@/components/charts/HeatMapChart';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';

const getSeriesKey = item => item.mountpoint;
const getValue = showMax => item => Math.round(item[showMax ? 'max_val' : 'avg_val']);

const COLOR_SETTINGS = [
	{ maxValue: 500, color: Consts.HEAT_MAP_COLORS.blue, label: '< 500 ms' },
	{ maxValue: 1000, color: Consts.HEAT_MAP_COLORS.green, label: '500 – 1 000' },
	{ maxValue: 1500, color: Consts.HEAT_MAP_COLORS.yellow, label: '1 000 – 1 500' },
	{ maxValue: 100000000, color: Consts.HEAT_MAP_COLORS.red, label: '> 1 500 ms' },
];

export default {
	components: {
		Row, Col, Card, Affix,
		TimeSeriesChart, HeatMapChart, PopconfirmButton, StatsChartTitleWithDescription,
		VerticalLayout, LabeledSwitch, DateRangeChooser, EmptyChartFiltersMessage,
		BaseStationSelect, SubNetworkSelect,
	},
	setup() {
		const { apiData, isLoading, doRequest } = Api.useDelaysGet();
		const showMax = ref(false);
		const showMaxLocal = ref(false);
		const showMaxLoading = ref(false);
		const sessionStore = useSessionStore();
		const { datesRange, datesRangeStr, datesRangeSafe, rangeDays } = useDateRangeAdapter(sessionStore, 1, 0);
		const datesRangeLocal = ref(datesRangeSafe.value);
		const rangeDaysLocal = ref(rangeDays.value);
		const isLoadingTestData = ref(false);

		const mountpointsSelected = ref([]);

		const filteredDelays = computed(() => {
			if (mountpointsSelected.value.length === 0) {
				return [];
			}
			return apiData.value.delays.filter(item => mountpointsSelected.value.includes(item.mountpoint));
		});

		const heatmapSeriesCategories = computed(() => generateSeries({
			items: filteredDelays.value,
			datetimeField: 'start',
			getSeriesKeyFn: getSeriesKey,
			getValueFn: getValue(showMaxLocal.value),
			seriesStep: getClosestValue(apiData.value.interval * (rangeDaysLocal.value || 1) * 2, MINUTE_AGGREGATES),
			categoryFormatter: datetimeToString,
			pointEntryFactory: pointEntryFactoryXYDay,
			showNullAsZero: false,
			minDate: new Date(datesRangeLocal.value[0]),
			maxDate: new Date(datesRangeLocal.value[1]),
			forceSeriesKeys: mountpointsSelected.value,
			roundTimeValues: true,
		}));
		const heatmapSeries = computed(() => heatmapSeriesCategories.value.series);
		const heatmapCategories = computed(() => heatmapSeriesCategories.value.categories);

		const lineSeries = computed(() => generateSeries({
			items: filteredDelays.value,
			datetimeField: 'start',
			getSeriesKeyFn: getSeriesKey,
			getValueFn: getValue(showMaxLocal.value),
			seriesStep: getClosestValue(apiData.value.interval * (rangeDaysLocal.value || 1) * 2, MINUTE_AGGREGATES),
			showNullAsZero: false,
			minDate: new Date(datesRangeLocal.value[0]),
			maxDate: new Date(datesRangeLocal.value[1]),
			roundTimeValues: true,
		}).series);
		const legendHeightBase = computed(() => Math.ceil(lineSeries.value.length / 13) * 16);
		const lineChartHeight = computed(() => 400 + Math.min(legendHeightBase.value, 200));
		const lineChartOptions = computed(() => {
			let options = getBaseOptions('line_delays', lineSeries.value);
			setXAxisRange(options, datesRangeLocal.value);
			options.stroke.dashArray = generateDashArray(lineSeries.value);
			options.legend.height = (legendHeightBase.value >= 200) ? 200 : null;
			options.legend.showForSingleSeries = true;
			return options;
		});

		const delaysGet = () => {
			if (datesRangeStr.value != null) {
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
				});
				datesRangeLocal.value = datesRangeSafe.value;
				rangeDaysLocal.value = rangeDays.value;
			}
		};
		onMounted(delaysGet);

		const {
			subNetworkSelect,
			setMountpointsForSubnetwork,
			setSubnetworkOnMountpointsChange,
		} = useSubNetworkMountpointsFilter(mountpointsSelected, delaysGet);

		const onGenerateTestData = () => {
			isLoadingTestData.value = true;
			Api.generateTestDelays(() => {
				isLoadingTestData.value = false;
				delaysGet();
			})
		};

		const onShowMaxChange = () => {
			showMaxLoading.value = true;
			setTimeout(() => {
				showMaxLoading.value = false;
				showMaxLocal.value = showMax.value;
			}, 200);
		};

		const { injectValue } = useProvideInjectWrapper;
		const wrapper = injectValue();

		const showGenerateTestDataButton = computed(() => sessionStore.debug);

		return {
			isLoading,
			showMax,
			showMaxLoading,
			onShowMaxChange,
			datesRange,
			isLoadingTestData,
			mountpointsSelected,
			subNetworkSelect,
			heatmapSeries,
			heatmapCategories,
			lineSeries,
			lineChartHeight,
			lineChartOptions,
			setMountpointsForSubnetwork,
			setSubnetworkOnMountpointsChange,
			delaysGet,
			onGenerateTestData,
			wrapper,
			showGenerateTestDataButton,
			COLOR_SETTINGS: ref(COLOR_SETTINGS),
		};
	}
}
</script>
